import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { IconComponent } from '../icon/icon.component';

/**
 * Identity an option renders as an avatar. Deliberately a flat value object
 * rather than a domain user: this primitive lives in `ui/` and must not reach
 * into a feature's models. Callers build it from `getUserColor` /
 * `userInitials` so the colour mapping stays in one place.
 */
export interface IFilterSelectAvatar {
  /** Already-uppercased letters shown when there is no photo. */
  initials: string;
  /** Background behind the initials. */
  color: string;
  /** Base64 JPEG payload WITHOUT the `data:` prefix, as the users API returns it. */
  image?: string | null;
}

export interface IFilterSelectOption {
  /**
   * `null` marks the unfiltered option ("Any status" / "Anyone"). The component
   * treats it as "nothing selected" and renders the trigger in its idle state.
   */
  value: string | number | null;
  /** Already-translated text — this primitive does not translate option labels. */
  label: string;
  /**
   * Muted second line under the label — an email, typically. Matches the
   * two-line person row the inline owner picker on the orders table uses.
   */
  sublabel?: string;
  /** Optional trailing count. `null`/omitted renders nothing, `0` renders "0". */
  count?: number | null;
  /** Solid dot shown before the label, for status-like options. */
  dotColor?: string;
  /**
   * Colours the trigger takes while this option is the selection, instead of
   * the default purple active treatment. Used by the status filter so a chosen
   * status wears the same colours as its badge in the table.
   */
  activeStyle?: { [key: string]: string };
  /** Avatar shown before the label, for people. Wins over `dotColor`. */
  avatar?: IFilterSelectAvatar;
  /** Draws a divider above this option — for options that mean something else. */
  separatorBefore?: boolean;
  /**
   * Short chip after the label, e.g. "ME" on the signed-in user's row. Already
   * translated and already cased.
   */
  badge?: string;
}

/**
 * Quick-filter dropdown: a compact trigger (icon or avatar + label + chevron)
 * that opens a menu of single-select options.
 *
 * The trigger follows the smart-inbox assignee control in the conversation
 * header, and the option rows follow the inbox advanced filter's picklist, so
 * the two surfaces read as the same control (WB-8073).
 *
 * Active state reuses the product's one applied-filter treatment — the same
 * purple border + 8% wash + purple text as `.ui-header-action--active` in
 * `ui-page-header` — rather than inventing a second "this filter is on" look.
 *
 * Stateless: the host owns `value` and re-renders on `valueChange`. Nothing is
 * selected locally, so a rejected change (permissions, a failed request) simply
 * never comes back as a new `value`.
 */
@Component({
  selector: 'ui-filter-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    TranslateModule,
    IconComponent,
  ],
  templateUrl: './filter-select.component.html',
  styleUrls: ['./filter-select.component.scss'],
})
export class FilterSelectComponent implements OnInit, OnDestroy {
  /** Trigger text while nothing is selected, e.g. "Any status". Already translated. */
  @Input() placeholder = '';

  /** Lucide icon name shown on the trigger (see `VOLTA_ICONS`). */
  @Input() icon = '';

  @Input() options: IFilterSelectOption[] = [];

  /** Currently selected option value. `null` = unfiltered. */
  @Input() value: string | number | null = null;

  /** Adds a search box above the list and turns on `searchChange`. */
  @Input() searchable = false;

  /** Already-translated placeholder for the search box. */
  @Input() searchPlaceholder = '';

  /** Spinner in the search box while the host resolves a query. */
  @Input() loading = false;

  /** Already-translated text shown when `options` is empty. */
  @Input() emptyLabel = '';

  /** Accessible name for the trigger button. */
  @Input() ariaLabel = '';

  /**
   * `solid` is a filter in its own right. `ghost` is an overflow control — a
   * dashed, quieter trigger for "the options that did not fit" — so it reads as
   * a way in to more filters rather than as a fourth filter sitting beside them.
   */
  @Input() variant: 'solid' | 'ghost' = 'solid';

  /**
   * `rounded` is the 8px control shape shared with ui-button. `pill` is for
   * filters whose values are themselves rendered as pills elsewhere — the status
   * filter, whose selection matches the pill badges in the table rows.
   */
  @Input() shape: 'rounded' | 'pill' = 'rounded';

  @Output() valueChange = new EventEmitter<string | number | null>();

  /** Debounced search text. Only fires when `searchable` is on. */
  @Output() searchChange = new EventEmitter<string>();

  /** Fires when the menu opens, so hosts can lazy-load their options. */
  @Output() opened = new EventEmitter<void>();

  searchControl = new FormControl<string>('', { nonNullable: true });

  /**
   * The panel stops click propagation so typing in the search box cannot close
   * the menu, which also stops Material's own close-on-select. Picking a value
   * therefore has to close the menu explicitly through the trigger.
   */
  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;

  private searchSub?: Subscription;

  ngOnInit(): void {
    if (!this.searchable) return;
    this.searchSub = this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((term) => this.searchChange.emit(term));
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  /** The picked option, or `undefined` while the filter is unset. */
  get selected(): IFilterSelectOption | undefined {
    if (this.value === null || this.value === undefined) return undefined;
    return this.options.find((option) => option.value === this.value);
  }

  /**
   * True once a filtering value is set. Driven by `value`, not by `selected`,
   * so the trigger still reads as active while the option list is still
   * loading and the picked option cannot be resolved to a label yet.
   */
  get isActive(): boolean {
    return this.value !== null && this.value !== undefined;
  }

  /**
   * Label on the trigger: the selected option's when it is known, the raw value
   * as a last resort (a filter restored from the URL before its list arrives),
   * and the placeholder while unset.
   */
  get triggerLabel(): string {
    if (!this.isActive) return this.placeholder;
    return this.selected?.label ?? String(this.value);
  }

  /** Per-option colours for the trigger, when the picked option supplies them. */
  get activeTriggerStyle(): { [key: string]: string } {
    if (!this.isActive) return {};
    return this.selected?.activeStyle ?? {};
  }

  onOpen(): void {
    this.opened.emit();
  }

  /**
   * Keeps typing in the search box from reaching MatMenu — its `_handleKeydown`
   * sits on an ancestor and would close the menu — while letting through the two
   * keys that are the menu's to handle. Escape especially: it is the standard
   * way out of an open menu, and a blanket guard here made the panel a keyboard
   * trap once focus was inside it.
   */
  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Tab') return;
    event.stopPropagation();
  }

  /**
   * True when the list holds nothing to filter BY — only the reset option, which
   * is always present. Without this the "no results" state would be unreachable
   * for a searchable list, since `options` is never literally empty.
   */
  get isEmpty(): boolean {
    return !this.options.some((option) => option.value !== null);
  }

  select(option: IFilterSelectOption): void {
    this.menuTrigger?.closeMenu();
    if (option.value === this.value) return;
    this.valueChange.emit(option.value);
  }

  isSelected(option: IFilterSelectOption): boolean {
    return option.value === this.value;
  }

  /** Keeps `*ngFor` from tearing down every row when a search re-resolves the list. */
  trackByValue(_index: number, option: IFilterSelectOption): string {
    return `${option.value}`;
  }
}
